import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	Output,
	TemplateRef,
} from '@angular/core';
import { fade, openClose } from '@core/animations/animations';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from '@core/base.component';
import { ICON } from '@core/util/icon';
import {
	AlgoliaFinancialInstitution,
	AlgoliaFinancialInstitutionBranch,
} from '@fnModels/algolia/financial_institutions';
import { MatterFileType } from '@models/matter';
import {
	FinancialInstitutionLender,
	MortgageeType,
	PopularLenders,
} from '@models/lender';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { InstitutionRepo } from '@repos/institution/institution.service';
import { InstitutionBranchRepo } from '@repos/institutionBranch/institution-branch.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { FinancialInstitutionBranchSearchState } from '@state/algolia-search/financial-institution-branch-search';
import {
	FinancialInstitutionSearchDispatch,
	FinancialInstitutionSearchState,
} from '@state/algolia-search/financial-institution-search';
import { Action } from '@state/utils/models';
import LogRocket from 'logrocket';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { CreateOrUpdateInstitutionModel } from 'src/app/private/sale/encumbrances/encumbrance/components/institution/create-branch/create-branch.component';
import {
	CreateInstitutionComponent,
	CreateInstitutionData,
} from 'src/app/private/sale/encumbrances/encumbrance/components/institution/create-institution/create-institution.component';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';

export enum DropdownSelect {
	CREATE = 'create',
	SELECT_INSTITUTION = 'selectInstitution',
	SELECT_BRANCH = 'selectBranch',
}

export interface onSelectInstitution {
	financialInstitution: FinancialInstitutionLender;
	action: Action;
}

interface VM {
	selectedInstitution: FinancialInstitutionLender;
}

const defaultVM = {
	selectedInstitution: null,
};

@UntilDestroy()
@Component({
	selector: 'app-lender-search-input-v2',
	templateUrl: './lender-search-input.component.html',
	animations: [openClose(), fade()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LenderSearchInputV2Component extends BaseComponent<VM> {
	public loading = false;
	public ICON = ICON;
	public financialInstitutionName = '';
	public institutionModel: CreateOrUpdateInstitutionModel;
	public dropdownSelect = DropdownSelect;
	public lenders = PopularLenders;
	public popularLenders = PopularLenders;
	public mortgageeTypes = MortgageeType;
	public financialInstitutions: FinancialInstitutionLender[];

	@Output() onSelected = new EventEmitter<onSelectInstitution>();
	@Output() onCreate = new EventEmitter<string>();

	@Input() mortgageeType: MortgageeType = MortgageeType.INSTITUTION;
	@Input() includeFinancialInstitutions: boolean;
	@Input() includeTrustCompanies: boolean;
	@Input() fileType: MatterFileType;
	@Input() showDischargeFields: boolean;
	@Input() set selectedInstitution(
		selectedInstitution: FinancialInstitutionLender,
	) {
		this.VM = {
			selectedInstitution,
		};
	}

	@Input() label: string = 'Institution';
	@Input() required: boolean = false;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	@Input() labelTemplate: TemplateRef<any>;
	@Select(FinancialInstitutionSearchState.getList)
	financialInstitutions$: Observable<FinancialInstitutionLender[]>;
	@ViewSelectSnapshot(FinancialInstitutionBranchSearchState.getList)
	financialInstitutionsBranches;

	constructor(
		private institutionDispatch: FinancialInstitutionSearchDispatch,
		private matDialog: MatDialog,
		private institutionRepo: InstitutionRepo,
		private branchRepo: InstitutionBranchRepo,
		private snackbar: SnackbarService,
	) {
		super(defaultVM);
	}

	search = (ev) => {
		this.institutionDispatch.Search(ev, {
			creditUnion: true,
			generalFinancialInstitution: true,
			trustCompany: true,
		});
	};

	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	onSelect = async (
		ev: DropdownResult<
			Partial<AlgoliaFinancialInstitution | AlgoliaFinancialInstitutionBranch>
		>,
	) => {
		this.loading = true;
		if (ev.groupType === DropdownSelect.SELECT_INSTITUTION) {
			try {
				// fetch institution
				const _doc = await this.institutionRepo
					.Get((ev.result as AlgoliaFinancialInstitution)?.id)
					.pipe(take(1))
					.toPromise();

				const financialInstitution = {
					...(_doc.data() as FinancialInstitutionLender),
					id: _doc.id,
				};
				this.onSelected.emit({
					financialInstitution,
					action: Action.SET,
				});
				this.VM = {
					selectedInstitution: financialInstitution,
				};
			} catch (err) {
				LogRocket.error('Unable to select financial institution', err);
				console.log('Unable to select financial institution', err);
			} finally {
				this.loading = false;
			}
		} else if (ev.groupType === DropdownSelect.SELECT_BRANCH) {
			try {
				const _doc = await this.institutionRepo
					.Get((ev.result as AlgoliaFinancialInstitutionBranch)?.institutionId)
					.pipe(take(1), untilDestroyed(this))
					.toPromise();
				const _branchDoc = await this.branchRepo
					.Get((ev.result as AlgoliaFinancialInstitutionBranch)?.id, {
						financialInstitutionId: (
							ev.result as AlgoliaFinancialInstitutionBranch
						)?.institutionId,
					})
					.pipe(take(1), untilDestroyed(this))
					.toPromise();

				const institution = {
					..._doc.data(),
					id: _doc.id,
					branch: { ..._branchDoc.data(), id: _branchDoc.id },
				};

				this.onSelected.emit({
					financialInstitution: institution as FinancialInstitutionLender,
					action: Action.SET,
				});
			} catch (err) {
				LogRocket.error('Unable to select financial institution branch', err);
				console.log('Unable to select financial institution branch', err);
			} finally {
				this.loading = false;
			}
		} else if (ev.groupType === DropdownSelect.CREATE) {
			// load create component
			const data: CreateInstitutionData = {
				showDischargeFields: this.showDischargeFields,
				fileType: this.fileType,
				create: true,
				institution: {
					name: this.financialInstitutionName,
				},
				type: this.mortgageeType,
			};
			this.matDialog
				.open(CreateInstitutionComponent, {
					data,
					width: '80vw',
					maxWidth: '800px',
				})
				.afterClosed()
				.pipe(take(1), untilDestroyed(this))
				.subscribe((result) => {
					if (result) {
						this.onSelected.emit({
							financialInstitution: result as FinancialInstitutionLender,
							action: Action.CREATE,
						});
						this.snackbar.openSnackBar({
							text: 'Lender details submitted for review.',
							type: 'success',
						});
					}
					this.loading = false;
				});
		}
	};

	setLeadingBank(institutionId: string) {
		this.onSelect({
			result: { id: institutionId },
			groupType: DropdownSelect.SELECT_INSTITUTION,
			group: 0,
		});
	}

	clearBank() {
		this.VM = {
			selectedInstitution: null,
		};
		this.onSelected.emit(null);
		this.financialInstitutionName = '';
		this.institutionDispatch.Reset();
	}
}
